import { buildPackagingBlock } from './buildPackagingBlock';
import { renderPackagingBlock } from './renderPackagingBlock';
import { formatMoney } from './formatMoney';
import { removeGiftOptions } from './removeGiftOptions';
import { CART_COPY } from './cartCopy';
import { logError } from '../../utilities/logging';

/** Shopify's `properties | json` is [key, value] pairs; the block wants an object. */
const withObjectProperties = (line) => (Array.isArray(line?.properties) ? { ...line, properties: Object.fromEntries(line.properties) } : line);

/**
 * <packaging-block data-giftwrap-key data-currency data-lines>: the cart drawer's packaging block for one
 * wrapped piece (ALBAU-884). cart-packaging-row.liquid writes the child lines as JSON; this renders them.
 */
export class PackagingBlockElement extends HTMLElement {
  connectedCallback() {
    if (this.dataset.rendered === 'true') return;
    this.dataset.rendered = 'true';
    const giftWrapKey = this.dataset.giftwrapKey || '';
    const currency = this.dataset.currency || 'GBP';
    try {
      const lines = JSON.parse(this.dataset.lines || '[]').map(withObjectProperties);
      const block = buildPackagingBlock({ lines });
      this.innerHTML = renderPackagingBlock(block, {
        id: `PackagingBlock-${giftWrapKey}`,
        money: (amount, options = {}) => formatMoney(amount, { currency, ...options })
      });
    } catch (error) {
      logError({ label: 'PackagingBlockElement', error, message: 'Failed to render packaging block', extraData: { giftWrapKey } });
      return;
    }
    this.addEventListener('click', (event) => this.onClick(event));
  }

  /** @param {MouseEvent} event */
  async onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    const toggle = target?.closest('.packaging-block__toggle');
    if (toggle) {
      const panels = this.querySelector('.packaging-block__panels');
      const expanded = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', String(!expanded));
      toggle.textContent = expanded ? CART_COPY.viewSelection : CART_COPY.closeSelection;
      if (panels) panels.hidden = expanded;
      return;
    }
    const remove = target?.closest('.packaging-block__remove');
    if (!remove || !(remove instanceof HTMLButtonElement) || remove.disabled) return;
    remove.disabled = true;
    remove.setAttribute('aria-busy', 'true');
    try {
      await removeGiftOptions({ giftWrapKey: this.dataset.giftwrapKey || '' });
    } catch (error) {
      logError({ label: 'PackagingBlockElement', error, message: 'Remove gifting options failed', extraData: { giftWrapKey: this.dataset.giftwrapKey } });
      const errors = document.getElementById('CartDrawer-CartErrors');
      if (errors) errors.textContent = /** @type {any} */ (window).cartStrings?.error || 'Error updating cart';
      remove.disabled = false;
      remove.removeAttribute('aria-busy');
    }
  }
}

/** Registers <packaging-block> once. */
export function definePackagingBlock() {
  if (!customElements.get('packaging-block')) customElements.define('packaging-block', PackagingBlockElement);
}
